import * as THREE from 'three';
import { DropInViewer, SceneFormat, SceneRevealMode } from '@mkkellogg/gaussian-splats-3d';

export interface LoadSplatResult {
  mesh: DropInViewer;
  ready: Promise<void>;
  /**
   * Splat-aware raycaster. Returns the world-space surface hit at the given
   * NDC coordinates, or null if the ray misses every splat. Wraps mkkellogg's
   * internal Raycaster (accessed via the Viewer's `raycaster` field — not in
   * the public type surface but stable since mkkellogg's own Viewer uses it
   * the same way internally). Falls back to null until the splat tree is
   * built (a few hundred ms after onSplatMeshChanged fires).
   */
  raycast: (
    camera: THREE.Camera,
    ndcX: number,
    ndcY: number,
    screenW: number,
    screenH: number
  ) => THREE.Vector3 | null;
}

export interface LoadSplatOptions {
  onProgress?: (loaded: number, total: number) => void;
}

interface MkRaycasterInternal {
  setFromCameraAndScreenPosition: (
    camera: THREE.Camera,
    screenPos: THREE.Vector2,
    screenDims: THREE.Vector2
  ) => void;
  intersectSplatMesh: (splatMesh: unknown, outHits: { origin: THREE.Vector3 }[]) => void;
}

interface MkViewerInternal {
  raycaster: MkRaycasterInternal;
  splatMesh: unknown;
}

export function loadSplat(
  scene: THREE.Scene,
  url: string,
  opts: LoadSplatOptions = {}
): LoadSplatResult {
  // ?nosh URL flag drops spherical-harmonics degree to 0 for perf diagnosis.
  // SH=2 stores view-dependent color (specular/wet look as camera moves) but
  // requires extra texture samples per splat per frame. SH=0 is "constant
  // color per splat" — cheapest path; visually slightly flatter.
  const noSh = new URLSearchParams(location.search).has('nosh');
  const mesh = new DropInViewer({
    sphericalHarmonicsDegree: noSh ? 0 : 2,
    // `integerBasedSort: false` uses float-precision distances for splat
    // sort. mkkellogg's int-based path was tuned for older hardware where
    // float math was relatively expensive; on anything modern, float is
    // a wash or faster, and integer quantization can produce subtle sort
    // ordering inversions visible as flicker on overlapping splats.
    //
    // `sharedMemoryForWorkers: false` is required because we don't ship
    // Cross-Origin-Opener-Policy / Embedder-Policy headers — without them
    // SharedArrayBuffer isn't available and mkkellogg's default sort worker
    // throws DataCloneError trying to postMessage one. The lib also
    // auto-falls-back to false at runtime when SAB isn't available, so
    // this explicit setting just makes the intent visible.
    integerBasedSort: false,
    sharedMemoryForWorkers: false,
    enableOptionalEffects: true,
    sceneRevealMode: SceneRevealMode.Instant,
    rootElement: null
  });

  // Load .compressed.ply — parsed natively by mkkellogg's
  // PlayCanvasCompressedPlyParser. On disk we only ship the Brotli-
  // precompressed .br variant (~16MB); nginx serves it under the
  // URL /scene.compressed.ply with Content-Encoding: br and the
  // browser's built-in decoder inflates transparently before mkkellogg
  // sees the bytes. See README "Splat asset" for the format-iteration
  // history and the roadmap entry on switching to a more compact
  // native format once mkkellogg supports newer SPZ versions.
  const ready = (async () => {
    await mesh.addSplatScene(url, {
      showLoadingUI: false,
      // Progressive loading is only supported for raw .ply and .ksplat.
      // .compressed.ply must be fully downloaded before parsing.
      progressiveLoad: false,
      format: SceneFormat.Ply,
      onProgress: (pct: number) => {
        if (opts.onProgress) opts.onProgress(pct ?? 0, 100);
      }
    });
  })();

  scene.add(mesh);

  // Build the raycaster closure. Allocate working vectors once.
  const screenPos = new THREE.Vector2();
  const screenDims = new THREE.Vector2();
  const raycast = (
    camera: THREE.Camera,
    ndcX: number,
    ndcY: number,
    screenW: number,
    screenH: number
  ): THREE.Vector3 | null => {
    const viewer = (mesh as unknown as { viewer: MkViewerInternal }).viewer;
    if (!viewer?.raycaster || !viewer.splatMesh) return null;

    // mkkellogg's raycaster expects canvas-pixel coords with canvas-pixel
    // dims. Three.js's setViewOffset modifies the camera's projection matrix
    // so that canvas-NDC [-1,1] already maps to the visible canvas slice of
    // the virtual frame — so the standard unproject path works correctly
    // without us translating to virtual-pixel space. (An earlier attempt to
    // pass virtual pixels was wrong: it caused unproject to interpret the NDC
    // as virtual-frame coordinates, producing rays offset by ~panelW/2 toward
    // screen center.)
    screenPos.set(((ndcX + 1) / 2) * screenW, ((1 - ndcY) / 2) * screenH);
    screenDims.set(screenW, screenH);
    viewer.raycaster.setFromCameraAndScreenPosition(camera, screenPos, screenDims);
    const hits: { origin: THREE.Vector3 }[] = [];
    viewer.raycaster.intersectSplatMesh(viewer.splatMesh, hits);
    if (hits.length === 0) return null;
    // intersectSplatMesh returns hits in ray order; the first one is closest.
    return hits[0]!.origin.clone();
  };

  return { mesh, ready, raycast };
}
